import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Chip,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DataGridPro } from "@mui/x-data-grid-pro";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  getDdpImportTarifas,
  saveDdpImportTarifa,
} from "../../api/Estimates/ddpImportTarifasApi";
import { tipoCalculoLabel } from "../../utils/ddpImportTarifasPure";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageMobileCardSx,
  pageDataGridSx,
} from "../../styles/pageModernStyles";

const TIPO_CHIP = {
  per_cbm: "info",
  fijo_envio: "primary",
  pct_seguro: "secondary",
  pct_arancel: "warning",
};

const DdpImportTarifas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({
    valor: "",
    baseMultiplicador: "",
    activo: true,
  });
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      width: "100%",
      minWidth: 0,
    }),
    [theme],
  );

  const toolbarIconSx = useMemo(
    () => ({
      border: "1px solid",
      borderColor: alpha(accent, 0.22),
      backgroundColor: theme.palette.background.paper,
      color: accent,
      width: { xs: 40, md: 34 },
      height: { xs: 40, md: 34 },
      minWidth: { xs: 40, md: 34 },
      minHeight: { xs: 40, md: 34 },
      p: 0,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
      "&:hover": {
        backgroundColor: alpha(accent, 0.1),
        borderColor: accent,
      },
      "& .MuiSvgIcon-root": { fontSize: "1.05rem" },
    }),
    [theme, accent],
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const tarifas = await getDdpImportTarifas();
      setRows(
        tarifas.map((row) => ({
          id: row.subgrupoId,
          ...row,
        })),
      );
    } catch (err) {
      console.error("Error cargando tarifas DDP:", err);
      setSnack({
        open: true,
        severity: "error",
        message: t("DdpImportTarifasPage.saveError"),
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.activo !== false).length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.concepto,
        row.subgrupoId,
        tipoCalculoLabel(row.tipoCalculo),
        String(row.valor ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const formatTarifaInput = (val, decimals = 2) => {
    if (val === null || val === undefined || val === "") return "";
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(decimals) : String(val);
  };

  const openEdit = useCallback((row) => {
    setEditRow(row);
    setForm({
      valor: formatTarifaInput(row.valor),
      baseMultiplicador: formatTarifaInput(row.baseMultiplicador),
      activo: row.activo !== false,
    });
  }, []);

  const handleSave = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const saved = await saveDdpImportTarifa(editRow.subgrupoId, {
        valor: form.valor,
        baseMultiplicador:
          editRow.tipoCalculo === "pct_seguro" ? form.baseMultiplicador : null,
        activo: form.activo,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.subgrupoId === saved.subgrupoId
            ? { id: saved.subgrupoId, ...saved }
            : r,
        ),
      );
      setEditRow(null);
    } catch (err) {
      console.error("Error guardando tarifa:", err);
      setSnack({
        open: true,
        severity: "error",
        message: t("DdpImportTarifasPage.saveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const valorLabel = (tipoCalculo) => {
    switch (tipoCalculo) {
      case "per_cbm":
        return t("DdpImportTarifasPage.valorPerCbm");
      case "fijo_envio":
        return t("DdpImportTarifasPage.valorFixed");
      case "pct_seguro":
      case "pct_arancel":
        return t("DdpImportTarifasPage.valorPercent");
      default:
        return t("DdpImportTarifasPage.valor");
    }
  };

  const editAccent = editRow
    ? theme.palette[TIPO_CHIP[editRow.tipoCalculo] || "primary"].main
    : accent;

  const renderTipoChip = (tipoCalculo) => {
    const color = TIPO_CHIP[tipoCalculo] || "default";
    return (
      <Chip
        label={tipoCalculoLabel(tipoCalculo)}
        size="small"
        color={color === "default" ? undefined : color}
        variant="outlined"
        sx={{ fontWeight: 600, height: 24, fontSize: "0.7rem" }}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        field: "subgrupoId",
        headerName: t("DdpImportTarifasPage.colId"),
        width: 72,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: "0.7rem",
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.action.selected
                  : theme.palette.grey[100],
            }}
          />
        ),
      },
      {
        field: "concepto",
        headerName: t("DdpImportTarifasPage.colConcept"),
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, fontSize: "0.8125rem" }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "tipoCalculo",
        headerName: t("DdpImportTarifasPage.colCalc"),
        width: 188,
        renderCell: (params) => renderTipoChip(params.value),
      },
      {
        field: "valor",
        headerName: t("DdpImportTarifasPage.colValor"),
        width: 108,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
          >
            {params.value != null ? Number(params.value).toFixed(2) : "—"}
          </Typography>
        ),
      },
      {
        field: "baseMultiplicador",
        headerName: t("DdpImportTarifasPage.colMult"),
        width: 88,
        align: "right",
        headerAlign: "right",
        renderCell: (params) =>
          params.value != null ? (
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {Number(params.value).toFixed(2)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: "activo",
        headerName: t("DdpImportTarifasPage.colActive"),
        width: 96,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.value
                ? t("DdpImportTarifasPage.activeYes")
                : t("DdpImportTarifasPage.activeNo")
            }
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
            sx={{ height: 22, fontWeight: 700, fontSize: "0.7rem" }}
          />
        ),
      },
      {
        field: "updatedAt",
        headerName: t("DdpImportTarifasPage.colUpdated"),
        width: 152,
        renderCell: (params) => (
          <Typography variant="caption" color="text.secondary">
            {params.value ? new Date(params.value).toLocaleString() : "—"}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 52,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={t("DdpImportTarifasPage.edit")}>
            <span>
              <IconButton
                size="small"
                onClick={() => openEdit(params.row)}
                sx={{
                  border: 1,
                  borderColor: alpha(accent, 0.3),
                  bgcolor: alpha(accent, 0.08),
                  "&:hover": { bgcolor: alpha(accent, 0.16) },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [t, theme, accent, openEdit],
  );

  const renderMobileCard = (row) => (
    <Box
      key={row.id}
      onClick={() => openEdit(row)}
      sx={{
        ...pageMobileCardSx(theme, {
          active: row.activo !== false,
          accentColor:
            row.activo !== false ? accent : theme.palette.text.secondary,
        }),
        cursor: "pointer",
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Chip
            label={row.subgrupoId}
            size="small"
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: "0.68rem",
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", lineHeight: 1.3 }}>
              {row.concepto}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{renderTipoChip(row.tipoCalculo)}</Box>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
            sx={{
              border: 1,
              borderColor: alpha(accent, 0.3),
              flexShrink: 0,
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip
            size="small"
            label={`${t("DdpImportTarifasPage.colValor")}: ${
              row.valor != null ? Number(row.valor).toFixed(2) : "—"
            }`}
            sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
          />
          {row.baseMultiplicador != null ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${t("DdpImportTarifasPage.colMult")}: ${Number(row.baseMultiplicador).toFixed(2)}`}
              sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600 }}
            />
          ) : null}
          <Chip
            size="small"
            label={
              row.activo !== false
                ? t("DdpImportTarifasPage.activeYes")
                : t("DdpImportTarifasPage.activeNo")
            }
            color={row.activo !== false ? "success" : "default"}
            variant={row.activo !== false ? "filled" : "outlined"}
            sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
          />
        </Stack>

        {row.updatedAt ? (
          <Typography variant="caption" color="text.secondary">
            {new Date(row.updatedAt).toLocaleString()}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );

  const drawerHeaderBg =
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 48%, #f5faf7 100%)";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: { xs: "auto", md: "hidden" },
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        ...contentMargin,
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          ...pageHeroShellSx(theme, { sticky: !isMobile }),
          mb: 1.25,
          flexShrink: 0,
        }}
      >
        <Stack spacing={{ xs: 1.25, md: 1 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 1.25, md: 1 }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Box
                sx={{
                  width: { xs: 44, md: 40 },
                  height: { xs: 44, md: 40 },
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(accent, 0.22)
                      : "#d4f0f4",
                  color: theme.palette.primary.dark,
                }}
              >
                <LocalShippingOutlinedIcon sx={{ fontSize: { xs: 22, md: 20 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1, lineHeight: 1.2 }}
                >
                  LOGÍSTICA · ESTIMATES
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.05rem", md: "1.15rem" },
                  }}
                >
                  {t("DdpImportTarifasPage.title")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.5, rowGap: 0.35 }}
                >
                  <Chip
                    size="small"
                    label={t("DdpImportTarifasPage.kpiLines", {
                      count: stats.total,
                    })}
                    variant="outlined"
                    sx={pageKpiChipSx}
                  />
                  <Chip
                    size="small"
                    label={t("DdpImportTarifasPage.kpiActive", {
                      count: stats.active,
                    })}
                    sx={{
                      ...pageKpiChipSx,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.dark,
                    }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                size="small"
                placeholder={t("DdpImportTarifasPage.searchPlaceholder", {
                  defaultValue: "Buscar concepto…",
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  width: { xs: "100%", md: 220 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={t("DdpImportTarifasPage.refresh")}>
                <span>
                  <IconButton
                    onClick={fetchData}
                    disabled={loading}
                    aria-label={t("DdpImportTarifasPage.refresh")}
                    sx={toolbarIconSx}
                  >
                    {loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <RefreshOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.8rem",
              lineHeight: 1.45,
              display: { xs: "block", md: "block" },
            }}
          >
            {t("DdpImportTarifasPage.heroSubtitle")}
          </Typography>
        </Stack>
      </Box>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{
          mb: 1.25,
          borderRadius: 2,
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
        }}
      >
        {t("DdpImportTarifasPage.estimatesNote")}{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {isMobile
            ? t("DdpImportTarifasPage.tapToEdit", {
                defaultValue: "Toca una tarjeta para editar.",
              })
            : t("DdpImportTarifasPage.gridHint")}
        </Box>
      </Alert>

      {/* Grid / cards */}
      <Box
        sx={{
          ...pageContentCardSx(theme, accent),
          p: 0,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {isMobile ? (
              <Stack spacing={1.25}>
                {[...Array(5)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={108}
                    sx={{ borderRadius: 2.5 }}
                  />
                ))}
              </Stack>
            ) : (
              <Skeleton variant="rounded" height="100%" sx={{ minHeight: 320 }} />
            )}
          </Box>
        ) : filteredRows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              m: { xs: 1.5, md: 2 },
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2.5,
              borderStyle: "dashed",
              bgcolor: theme.palette.background.paper,
            }}
          >
            <LocalShippingOutlinedIcon
              sx={{ fontSize: 36, color: "action.disabled", mb: 1 }}
            />
            <Typography color="text.secondary" fontWeight={600}>
              {search.trim()
                ? t("DdpImportTarifasPage.noSearchResults", {
                    defaultValue: "No hay tarifas con esa búsqueda",
                  })
                : t("DdpImportTarifasPage.noData", {
                    defaultValue: "No hay tarifas configuradas",
                  })}
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 1.5,
            }}
          >
            <Stack spacing={1.25}>
              {filteredRows.map((row) => renderMobileCard(row))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, height: "100%", width: "100%" }}>
            <DataGridPro
              rows={filteredRows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              density="compact"
              onRowDoubleClick={(params) => openEdit(params.row)}
              sx={{
                ...pageDataGridSx(theme),
                height: "100%",
                border: "none",
                borderRadius: 0,
                cursor: "pointer",
              }}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={!!editRow}
        onClose={() => !saving && setEditRow(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            borderTop: isMobile ? 0 : `4px solid ${editAccent}`,
            bgcolor: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: 1.5, sm: 2 },
            pb: { xs: 1.25, sm: 1.5 },
            flexShrink: 0,
            background: drawerHeaderBg,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" fontWeight={800}>
            {t("DdpImportTarifasPage.editTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {editRow?.concepto}
          </Typography>
          {editRow ? (
            <Box sx={{ mt: 1 }}>{renderTipoChip(editRow.tipoCalculo)}</Box>
          ) : null}
        </Box>

        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2,
            px: { xs: 1.5, sm: 2.5 },
            bgcolor: theme.palette.background.default,
          }}
        >
          <TextField
            label={editRow ? valorLabel(editRow.tipoCalculo) : ""}
            type="number"
            value={form.valor}
            onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
            inputProps={{ step: "0.01", min: 0 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          {editRow?.tipoCalculo === "pct_seguro" ? (
            <TextField
              label={t("DdpImportTarifasPage.baseMult")}
              type="number"
              value={form.baseMultiplicador}
              onChange={(e) =>
                setForm((f) => ({ ...f, baseMultiplicador: e.target.value }))
              }
              inputProps={{ step: "0.01", min: 0 }}
              fullWidth
              size="small"
              helperText={t("DdpImportTarifasPage.baseMultHelp")}
              sx={fieldSx}
            />
          ) : null}
          <FormControlLabel
            control={
              <Switch
                checked={form.activo}
                color="success"
                onChange={(e) =>
                  setForm((f) => ({ ...f, activo: e.target.checked }))
                }
              />
            }
            label={t("DdpImportTarifasPage.colActive")}
            sx={{ ml: 0.25 }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            gap: 1,
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            pb: { xs: "max(1.5rem, env(safe-area-inset-bottom))", sm: 2 },
          }}
        >
          <Button
            onClick={() => setEditRow(null)}
            disabled={saving}
            variant="outlined"
            fullWidth={isMobile}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("DdpImportTarifasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            fullWidth={isMobile}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              ...pagePrimaryButtonSx(theme),
              bgcolor: editAccent,
              boxShadow: `0 4px 14px ${alpha(editAccent, 0.35)}`,
              "&:hover": { bgcolor: editAccent },
              textTransform: "none",
            }}
          >
            {t("DdpImportTarifasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DdpImportTarifas;
